import useChangesQuery from '~/hooks/services/servicenow/queries/useChangesQuery'

import moment from 'moment'
import { IoCheckmarkCircleOutline } from 'react-icons/io5'
import { useNavigate } from 'react-router'

import { Tile, TileFooter, TileHeader, TileListContent, TileListItem } from '../Tile'

const sevenDaysAgo = moment().subtract(7, 'days').startOf('day')
const today = moment().endOf('day')
const dateRange = [sevenDaysAgo, today] as const

const CompletedChangesTile = () => {
  const navigate = useNavigate()

  const { data: completedChanges, isLoading: isCompletedChangesLoading } = useChangesQuery({
    startDate: dateRange[0],
    endDate: dateRange[1],
    keyword: null,
    classification: null,
    category: null,
    state: ['Closed'],
    orderBy: 'ends',
    orderByDesc: true,
    take: 3,
    skip: 0,
    period: 'custom'
  })

  const formatDateRange = (starts: string, ends: string) => {
    const startDate = moment(starts)
    const endDate = moment(ends)

    if (startDate.isSame(endDate, 'day')) {
      return `${startDate.format('DD/MM/YY HH:mm')} - ${endDate.format('HH:mm')}`
    }
    return `${startDate.format('DD/MM/YY HH:mm')} - ${endDate.format('DD/MM/YY HH:mm')}`
  }

  /**
   * Navigates to the Changes page with a filter for closed changes and a date range for the last 7 days.
   * The ScrollToTop component will automatically reset the scroll position using SmoothScrollbar
   * since this navigation originates from a dashboard tile.
   */
  const handleNavigate = () => {
    const startDateFormatted = sevenDaysAgo.format('YYYY-MM-DD')
    const endDateFormatted = today.format('YYYY-MM-DD')
    navigate(`/changes?filter=closed&dateRange=${startDateFormatted},${endDateFormatted}`)
  }

  return (
    <Tile onMouseDown={completedChanges?.total_count === 0 ? handleNavigate : undefined}>
      <TileHeader loading={isCompletedChangesLoading}>
        <TileHeader.Title>{completedChanges?.total_count ?? 0}</TileHeader.Title>
        <TileHeader.Subtitle>Completed Changes (7 days)</TileHeader.Subtitle>
        <TileHeader.Item>
          <IoCheckmarkCircleOutline className="h-10 w-10 text-th-success" />
        </TileHeader.Item>
      </TileHeader>
      <TileListContent>
        {completedChanges?.changes.map((change) => (
          <TileListItem key={change.id} onMouseDown={() => navigate(`/changes/${change.id}`)}>
            <div className="opacity-60">
              <TileListItem.Title>{change.number}</TileListItem.Title>
              <TileListItem.Subtitle>{change.title}</TileListItem.Subtitle>
              <div className="mt-1 text-xs text-th-text-secondary">{formatDateRange(change.starts, change.ends)}</div>
            </div>
          </TileListItem>
        ))}
      </TileListContent>
      {(completedChanges?.total_count ?? 0) > 3 && (
        <TileFooter onMouseDown={handleNavigate}>View {(completedChanges?.total_count ?? 0) - 3} more...</TileFooter>
      )}
    </Tile>
  )
}

export default CompletedChangesTile
